import { Component, inject, Input, OnInit } from '@angular/core';
import { InputNumberModule } from 'primeng/inputnumber';
import { PaginatorModule } from 'primeng/paginator';
import { TranslocoPipe } from '@jsverse/transloco';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { startWith, takeUntil } from 'rxjs';
import { CallidusCurrencyPipe } from '@shared/pipes/currency/callidus-currency.pipe';
import { PremiumDisplayComponent } from '@shared/components/premium-display/premium-display.component';
import { AdditionalCoverageBoxComponent } from '@shared/components/additional-coverage-box/additional-coverage-box.component';
import { CalculationErrorDisplayComponent } from '@shared/components/calculation-error-display/calculation-error-display.component';
import { BvsHaftpflichtFormModel } from '../../models/form/bvs-haftpflicht-form.model';
import { AdditionalCoverageBaseComponent } from '@shared/directives/additional-coverage-base-component.directive';
import { AdditionalCoverageBoxForEnumComponent } from '@shared/components/additional-coverage-for-enum/additional-coverage-box-for-enum.component';
import { environment } from '@environment/environment';
import {
  CalculationResultStatus,
  CallidusBvsHaftpflichtBoatInformation,
  CallidusBvsHaftpflichtFigures,
  CallidusBvsVersicherungspraemieHaftpflichtCalcDto,
  VersicherungspraemieCalculationResultDto
} from '@backend/models';
import { BvsHaftpflichtStateService } from '@shared/services/bvs-haftpflicht-state.service';
import { NgClass } from '@angular/common';
import { isFormGroupInvalidExcept } from '@shared/utils/form.util';

@Component({
  selector: 'app-additional-coverage-haftpflicht-data',
  imports: [
    InputNumberModule,
    PaginatorModule,
    TranslocoPipe,
    ReactiveFormsModule,
    PremiumDisplayComponent,
    AdditionalCoverageBoxComponent,
    CalculationErrorDisplayComponent,
    AdditionalCoverageBoxForEnumComponent,
    NgClass
  ],
  providers: [CallidusCurrencyPipe, BvsHaftpflichtStateService],
  templateUrl: './additional-coverage-haftpflicht-data.component.html',
  styleUrl: './additional-coverage-haftpflicht-data.component.scss'
})
export class AdditionalCoverageHaftpflichtDataComponent extends AdditionalCoverageBaseComponent implements OnInit {
  @Input() form!: FormGroup<BvsHaftpflichtFormModel>;

  private readonly bvsHaftpflichtStateService = inject(BvsHaftpflichtStateService);

  haftpflichtCalculationResult: VersicherungspraemieCalculationResultDto | null = null;
  showCalculationPremiumEnabled = environment.featureToggles.showCalculationPremiumEnabled;

  ngOnInit(): void {
    this.form.valueChanges
      .pipe(
        startWith(this.form.value),
        takeUntil(this.componentDestroyed$)
      )
      .subscribe(() => this.calculate());
  }

  calculate(): void {
    if (this.isFormInvalidForPremiumCalculation()) {
      return;
    }

    const calcDto = this.createCalcDto();

    this.bvsHaftpflichtStateService
      .calculateHaftpflichtPraemie(calcDto)
      .subscribe((calculationResult: VersicherungspraemieCalculationResultDto) => this.haftpflichtCalculationResult = calculationResult);
  }

  showInsurancePremium(): boolean {
    return !this.isFormInvalidForPremiumCalculation() &&
      this.haftpflichtCalculationResult !== null &&
      (this.haftpflichtCalculationResult.publicCalculationResultStatus === CalculationResultStatus.Ok ||
        this.haftpflichtCalculationResult.publicCalculationResultStatus === CalculationResultStatus.OkWithWarning);
  }

  showErrorMessages(): boolean {
    return this.isFormInvalidForPremiumCalculation() ||
      (this.haftpflichtCalculationResult !== null &&
        (this.haftpflichtCalculationResult.publicCalculationResultStatus === CalculationResultStatus.OkWithWarning ||
          this.haftpflichtCalculationResult.publicCalculationResultStatus === CalculationResultStatus.Error));

  }

  isFormInvalidForPremiumCalculation(): boolean {
    return isFormGroupInvalidExcept(this.form, ['customerData']);
  }

  createCalcDto(): CallidusBvsVersicherungspraemieHaftpflichtCalcDto {
    const boatValues = this.form.controls.bvsBoatInformation.getRawValue();
    const figureValues = this.form.controls.figures.getRawValue();

    return {
      callidusBvsHaftpflichtBoatInformation: {
        boatType: boatValues.boatType,
        boatConstructionType: boatValues.boatConstructionType ?? undefined,
        sailingArea: boatValues.sailingArea,
        countryRegistered: boatValues.countryRegistered,
        mastMaterial: boatValues.mastMaterial ?? undefined,
        isEnginePresent: boatValues.isEnginePresent ?? false,
        isGutachtenPresent: boatValues.isGutachtenPresent ?? false,
        isComfortAusstattungVorhanden: boatValues.isComfortAusstattungVorhanden ?? false,
        isSailPresent: boatValues.isSailPresent ?? false,
        maxKnotsOver40: boatValues.maxKnotsOver40 ?? false,
        isBeibootVorhanden: boatValues.isBeibootVorhanden ?? false,
        boatModel: boatValues.boatModel,
        boatYard: boatValues.boatYard,
        manufacturer: boatValues.manufacturer,
        yachtType: boatValues.yachtType,
        engineManufacturer: boatValues.engineManufacturer,
        engineModel: boatValues.engineModel,
        engineFuelType: boatValues.engineFuelType,
        mooringSummer: boatValues.mooringSummer,
        mooringWinter: boatValues.mooringWinter,
        fullWidth: boatValues.fullWidth,
        fullLength: boatValues.fullLength,
        engineHp: boatValues.engineHp,
        builtInEngineCount: boatValues.builtInEngineCount,
        builtInEngineHp: boatValues.builtInEngineHp,
        extraEngineCount: boatValues.extraEngineCount,
        extraEngineHp: boatValues.extraEngineHp,
        sailSurfaceInMetersSquared: boatValues.sailSurfaceInMetersSquared,
        beibootHp: boatValues.beibootHp
      } as CallidusBvsHaftpflichtBoatInformation,

      callidusBvsHaftpflichtFigures: {
        isRechtsschutzSelected: figureValues.isRechtsschutzSelected ?? false,
        isKeinSelbstbehaltBeiRechtsschutzSelected: figureValues.isKeinSelbstbehaltBeiRechtsschutzSelected ?? false,
        isCharterSelected: figureValues.isCharterSelected ?? false,
        callidusBvsVersicherungssummeKunde: figureValues.callidusBvsVersicherungssummeKunde
      } as CallidusBvsHaftpflichtFigures
    } as CallidusBvsVersicherungspraemieHaftpflichtCalcDto;
  }
}


